import ArrowFailureIcon from "@/assetsV3/Wallet/ArrowFailureIcon";
import ArrowSuccessIcon from "@/assetsV3/Wallet/ArrowSuccessIcon";
import WrappeSolIcon from "@/assetsV3/Wallet/WrappeSolIcon";
import Image from "next/image";
import { useEffect, useLayoutEffect, useMemo, useState } from "react";
import AssetsSkeleton from "../Skeletons/AssetsSkeleton";
import { ChainFilterValues } from "@/utils/explore/filterValues";
import { WalletListItem } from "@/types/IProfile";
import { fetchTokenMetadata } from "@/services/getTokenMetaDataBirdeye";
import { getTokenInfo } from "@/services/getTokenInfo";
import { IPortfolioAssets } from "@/services/getWalletPortfolio";
import useSize from "@/hooks/useSize";
import getRandomPfp from "@/utils/getRandomPfp";
import {
  NATIVE_TOKEN_COMMON_ADDRESS,
  SOLANA_ADDRESS,
  SOLANA_ALTER_ADDRESS,
} from "@/utils";
import { useAppSelector } from "@/redux/hooks";
import { CHAIN_TOKENS_LOGO } from "@/utils/wallet";
import {
  ChainIdEnum,
  DECIMALS_NATIVE_TOKENS,
  RPC_URLS,
} from "@/types/enums/WalletTypeEnum";
import { getTokenPricesFromCG } from "@/services/getTokenPricesFromCG";
import useSolanaTransaction from "@/callbacks/useSolanaTransaction";
import { useEVMTransaction } from "@/callbacks/useEVMTransaction";

interface Props {
  debouncedSearch: string;
  setIsOpen: (val: boolean) => void;
  setSelectedToken: (val: IPortfolioAssets) => void;
  refreshState: boolean;
}
export const nativeTokenList: IPortfolioAssets[] = [
  {
    address: SOLANA_ADDRESS,
    decimals: DECIMALS_NATIVE_TOKENS[ChainIdEnum.solana],
    balance: 0,
    uiAmount: 0,
    chainId: ChainIdEnum.solana,
    name: "SOL",
    symbol: "SOL",
    icon: "https://raw.githubusercontent.com/solana-labs/token-list/main/assets/mainnet/So11111111111111111111111111111111111111112/logo.png",
    logo_uri:
      "https://raw.githubusercontent.com/solana-labs/token-list/main/assets/mainnet/So11111111111111111111111111111111111111112/logo.png",
    priceUsd: 0,
    valueUsd: 0,
    priceChange24h: 0,
    valueChangeInUsd: 0,
    value: 0,
    valueKey: "solana",
  },
  {
    address: NATIVE_TOKEN_COMMON_ADDRESS,
    decimals: DECIMALS_NATIVE_TOKENS[ChainIdEnum.base],
    balance: 0,
    uiAmount: 0,
    chainId: ChainIdEnum.base,
    name: "eth",
    symbol: "eth",
    icon: "https://s2.coinmarketcap.com/static/img/coins/64x64/1027.png",
    logo_uri: "https://s2.coinmarketcap.com/static/img/coins/64x64/1027.png",
    priceUsd: 0,
    valueUsd: 0,
    priceChange24h: 0,
    valueChangeInUsd: 0,
    value: 0,
    valueKey: "ethereum",
  },
  //TODO-UNHIDE-CHAINS
  // {
  //   address: NATIVE_TOKEN_COMMON_ADDRESS,
  //   decimals: DECIMALS_NATIVE_TOKENS[ChainIdEnum.bsc],
  //   balance: 0,
  //   uiAmount: 0,
  //   chainId: ChainIdEnum.bsc,
  //   name: "BNB",
  //   symbol: "bnb",
  //   icon: "https://s2.coinmarketcap.com/static/img/coins/64x64/1839.png",
  //   logo_uri: "https://s2.coinmarketcap.com/static/img/coins/64x64/1839.png",
  //   priceUsd: 0,
  //   valueUsd: 0,
  //   priceChange24h: 0,
  //   valueChangeInUsd: 0,
  //   value: 0,
  //   valueKey: "binancecoin",
  // },

  // {
  //   address: NATIVE_TOKEN_COMMON_ADDRESS,
  //   decimals: DECIMALS_NATIVE_TOKENS[ChainIdEnum.sonic],
  //   balance: 0,
  //   uiAmount: 0,
  //   chainId: ChainIdEnum.sonic,
  //   name: "S",
  //   symbol: "S",
  //   icon: "https://s2.coinmarketcap.com/static/img/coins/64x64/32684.png",
  //   logo_uri: "https://s2.coinmarketcap.com/static/img/coins/64x64/32684.png",
  //   priceUsd: 0,
  //   valueUsd: 0,
  //   priceChange24h: 0,
  //   valueChangeInUsd: 0,
  //   value: 0,
  //   valueKey: "sonic-3",
  // },
  // {
  //   address: NATIVE_TOKEN_COMMON_ADDRESS,
  //   decimals: DECIMALS_NATIVE_TOKENS[ChainIdEnum.ethereum],
  //   balance: 0,
  //   uiAmount: 0,
  //   chainId: ChainIdEnum.ethereum,
  //   name: "eth",
  //   symbol: "eth",
  //   icon: "https://s2.coinmarketcap.com/static/img/coins/64x64/1027.png",
  //   logo_uri: "https://s2.coinmarketcap.com/static/img/coins/64x64/1027.png",
  //   priceUsd: 0,
  //   valueUsd: 0,
  //   priceChange24h: 0,
  //   valueChangeInUsd: 0,
  //   value: 0,
  //   valueKey: "ethereum",
  // },
];
const WithdrawTokenList: React.FC<Props> = ({
  debouncedSearch,
  setIsOpen,
  setSelectedToken,
  refreshState,
}) => {
  const [isListLoading, setIsloading] = useState<boolean>(false);
  const [selectedChain, setSelectedChain] = useState("chain");
  const windowSize = useSize();
  const { internalWalletList } = useAppSelector((state) => state.combineWallet);
  const { getSolBalanceByAddress } = useSolanaTransaction();
  const { fetchEVMBalance } = useEVMTransaction();

  const [tokens, setTokens] = useState<IPortfolioAssets[]>(nativeTokenList);

  const getWalletsByChain = (chainId: ChainIdEnum) => {
    return internalWalletList.find(
      (wallet) => wallet.chain.toLowerCase() === chainId.toLowerCase()
    );
  };

  useEffect(() => {
    const fetchData = async () => {
      setIsloading(true); // Start loading

      try {
        const cgPriceApiResponse = await getTokenPricesFromCG();

        const updatedTokens = await Promise.all(
          tokens.map(async (token) => {
            const _wallet = getWalletsByChain(token.chainId as ChainIdEnum);

            let balance = 0;
            let uiAmount = 0;
            if (_wallet) {
              uiAmount =
                token.chainId === ChainIdEnum.solana
                  ? (await getSolBalanceByAddress(_wallet.address)) ?? 0
                  : Number(
                      await fetchEVMBalance(
                        _wallet.address,
                        RPC_URLS[_wallet.chain as ChainIdEnum]
                      )
                    ) ?? 0;
              balance = uiAmount * 10 ** token.decimals;
            }

            const priceData = token.valueKey
              ? cgPriceApiResponse[token.valueKey] || {}
              : {};
            const priceUsd = priceData.usd || 0;
            const priceChange24h = priceData.usd_24h_change || 0;
            const valueUsd = uiAmount * priceUsd;
            const valueChangeInUsd = (valueUsd * priceChange24h) / 100;

            return {
              ...token,
              balance,
              uiAmount,
              priceUsd,
              priceChange24h,
              valueUsd,
              valueChangeInUsd,
            };
          })
        );

        setTokens(updatedTokens);
      } catch (error) {
        console.error("Error fetching token balances:", error);
      } finally {
        setIsloading(false); // Stop loading
      }
    };

    fetchData();
  }, [refreshState]);

  const filteredAssets = tokens.filter((asset) => {
    const search = debouncedSearch?.toLowerCase() || "";
    const symbol = asset.symbol?.toLowerCase() || "";
    const name = asset.name?.toLowerCase() || "";
    const chainId = asset.chainId?.toLowerCase() || "";

    // Apply search filtering
    const matchesSearch =
      !debouncedSearch ||
      symbol.includes(search) ||
      name.includes(search) ||
      search.includes(symbol) ||
      search.includes(name);

    // Apply filterValue conditions
    if (selectedChain === "chain") {
      return matchesSearch; // No chain restriction, just search filtering
    } else if (selectedChain === "solana") {
      return chainId === "solana" && matchesSearch;
    } else if (selectedChain === "bsc") {
      return chainId === "bsc" && matchesSearch;
    } else if (selectedChain === "base") {
      return chainId === "base" && matchesSearch;
    } else if (selectedChain === "sonic") {
      return chainId === "sonic" && matchesSearch;
    }else if (selectedChain === "ethereum") {
      return chainId === "ethereum" && matchesSearch;
    }

    return matchesSearch; // Default case if filterValue is not defined
  });

  const [isMobile, setIsMobile] = useState<boolean>(false);
  useLayoutEffect(() => {
    if (windowSize.width < 992) {
      setIsMobile(true);
    } else {
      setIsMobile(false);
    }
  }, [windowSize.width]);

  return (
    <div className="flex flex-col gap-3 p-6 sm:h-fit overflow-scroll no-scrollbar sm:pb-6">
      <div className="flex gap-2 w-[100%] overflow-scroll no-scrollbar">
        {ChainFilterValues.map((item, index) => (
          <button
            key={index}
            onClick={() => {
              if (index > 2) {
                return;
              }
              setSelectedChain(item.value);
            }}
            type="button"
            className={`relative h-[30px] p-[1px] px-2  rounded-[100px] font-bold text-base font-inter ${
              selectedChain === item.value
                ? " bg-gradient-to-r from-[#FFB628] to-[#EA6A23]"
                : "bg-outgray-graylighter"
            }  text-outext-main 
            ${index > 2 ? "opacity-[0.5] cursor-not-allowed" : ""}
            `}
          >
            {item.title.split(" ")[0]}
          </button>
        ))}
      </div>
      {isListLoading ? (
        isMobile ? (
          [1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((_, index) => (
            <AssetsSkeleton key={index} />
          ))
        ) : (
          [1, 2, 3, 4].map((_, index) => <AssetsSkeleton key={index} />)
        )
      ) : filteredAssets.length <= 0 ? (
        <div className="p-6 sm:pt-0 gap-[10px] h-[200px] ">
          <div className="flex flex-col h-full items-center justify-center p-6">
            <p className="font-bold text-[21px]  text-outext-sub">Empty</p>
            {debouncedSearch ? (
              <p className="text-normal text-base text-outext-sub text-center">
                You do not hold {debouncedSearch.trim()}.
              </p>
            ) : (
              <p className="text-normal text-base text-outext-sub text-center">
                You do not hold any tokens.
              </p>
            )}
          </div>
        </div>
      ) : (
        filteredAssets.map((asset, index) => {
          const tokenChain = Object.values(ChainIdEnum).includes(
            asset?.chainId.split("-")[0] as ChainIdEnum
          )
            ? (asset?.chainId.split("-")[0] as ChainIdEnum)
            : ChainIdEnum.solana; // Default fallback

          const Icon = CHAIN_TOKENS_LOGO[tokenChain];

          return (
            <div
              key={index}
              className="flex items-center cursor-pointer justify-between"
              onClick={() => {
                setSelectedToken(asset);
                setIsOpen(false);
              }}
            >
              <div className="flex items-center w-[40%] gap-3">
                <div className="relative">
                  <Image
                    src={asset?.logo_uri || "/pfps/pfp7.svg"}
                    alt={asset?.symbol ?? ""}
                    width={36}
                    height={36}
                    className="rounded-full w-9 h-9"
                    onError={async ({ currentTarget }) => {
                      currentTarget.onerror = null; // prevents looping
                      currentTarget.src = getRandomPfp();
                    }}
                  />
                  {Icon && (
                    <div className="absolute h-[18px] w-[18px] flex items-center justify-center right-[-5px] bottom-[-3px] rounded-full bg-outstate-whitecompact">
                      <Icon
                        className="h-[12px] w-[12px] fill-outstate-blackcompact"
                        fill="#171717"
                      />
                    </div>
                  )}{" "}
                </div>

                <div className="text-left flex flex-col gap-1">
                  <p className="text-sm font-bold text-outext-main uppercase leading-[22px]">
                    {asset?.symbol}
                  </p>
                  {/* <p className="text-sm font-medium text-outext-sub leading-[22px]">
                  MC: ${convertToShortScale(asset?.marketCap ?? 0, true)}
                </p> */}
                </div>
              </div>
              <div className="text-left w-[60px] sm:w-[100px] flex flex-col justify-start gap-1">
                <p className="text-sm font-bold text-outext-main leading-[22px]">
                  ${Math.trunc(asset?.valueUsd * 100) / 100}
                </p>
                <p className="text-sm font-medium text-outext-sub leading-[22px]">
                  {Math.trunc(asset?.uiAmount * 10000) / 10000}
                </p>
              </div>
              <div className="text-right flex w-[60px] sm:w-[100px] flex-col justify-end items-end gap-1">
                <p className="text-sm font-bold text-outext-main leading-[22px]">
                  {asset?.valueChangeInUsd != null &&
                  asset?.valueChangeInUsd !== 0
                    ? `$${Math.trunc(asset.valueChangeInUsd * 10000) / 10000}`
                    : "$__"}{" "}
                </p>
                {!asset?.priceChange24h || asset?.priceChange24h === 0 ? (
                  <p className="text-sm font-medium text-outext-sub leading-[22px]">
                    __%
                  </p>
                ) : asset?.priceChange24h < 0 ? (
                  <div className="flex items-center leading-[22px]">
                    <ArrowFailureIcon width={22} height={22} />
                    <p className="text-sm font-medium text-outred-error">
                      {asset?.priceChange24h?.toFixed(2) ?? 0}%
                    </p>
                  </div>
                ) : (
                  <div className="flex items-center leading-[22px]">
                    <ArrowSuccessIcon width={22} height={22} />
                    <p className="text-sm font-medium text-outgreen-success">
                      {asset?.priceChange24h?.toFixed(2) ?? 0}%
                    </p>
                  </div>
                )}
              </div>
            </div>
          );
        })
      )}
    </div>
  );
};

export default WithdrawTokenList;
